<script setup>
import { ref, onMounted, computed } from 'vue';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import StatCard from '@/pages/admin/StatCard.vue';
import TextInput from '@/components/forms/inputs/text-input.vue';
import SelectInput from '@/components/forms/inputs/select-input.vue';
import { useToast } from '@/composables/use-toast';
import { useLayoutComponents } from '@/composables/layout.js';
import {
  getPublicationRequests,
  reviewPublicationRequest,
} from '@/services/publicationRequestsService';

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
];

const requests = ref([]);
const { PrimaryButton, ButtonLoading, Toast } = useLayoutComponents();
const { addToast } = useToast();

const selectedStatusFilter = ref('pending');
const reviewingId = ref(null);
const isRejectLoading = ref(false);
const isAcceptLoading = ref(false);

const getRequests = async () => {
  try {
    requests.value = await getPublicationRequests();
  } catch (error) {
    console.error(error);
    addToast('Failed to load publication requests', 'error');
  }
};

const submitReview = async (status, id) => {
  try {
    reviewingId.value = id;
    if (status === 'rejected') {
      isRejectLoading.value = true;
    } else {
      isAcceptLoading.value = true;
    }
    await reviewPublicationRequest(id, status);
    addToast(
      status === 'approved' ? 'Publication approved' : 'Publication rejected',
      'success',
    );
    await getRequests();
  } catch (error) {
    console.error(error);
    addToast(
      error?.response?.data?.message || 'Failed to review publication request',
      'error',
    );
  } finally {
    reviewingId.value = null;
    isRejectLoading.value = false;
    isAcceptLoading.value = false;
  }
};

onMounted(() => {
  getRequests();
});

const getStatus = (status) => {
  const statusConfig = {
    pending: {
      classes:
        'bg-amber-100 text-amber-800 dark:bg-amber-600 dark:text-black font-semibold',
    },
    approved: {
      classes:
        'bg-emerald-100 text-emerald-800 dark:bg-emerald-600 dark:text-black font-semibold',
    },
    rejected: {
      classes:
        'bg-red-100 text-red-800 dark:bg-red-600 dark:text-black font-semibold',
    },
  };
  return {
    classes: statusConfig[status]?.classes || '',
    text: status ? status.charAt(0).toUpperCase() + status.slice(1) : '',
  };
};

const filteredRequests = computed(() => {
  let filtered = requests.value;

  if (selectedStatusFilter.value) {
    filtered = filtered.filter(
      (request) => request.status === selectedStatusFilter.value,
    );
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter((request) => {
      const name = request.user?.name?.toLowerCase() || '';
      const email = request.user?.email?.toLowerCase() || '';
      const listingName =
        request.listing?.name?.toLowerCase() ||
        request.form?.listing_name?.toLowerCase() ||
        '';
      return name.includes(query) || email.includes(query) || listingName.includes(query);
    });
  }

  return filtered;
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredRequests.value.length / itemsPerPage.value)),
);

const paginatedRequests = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredRequests.value.slice(start, start + itemsPerPage.value);
});

const formatDate = (date) =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const filterRequests = (status) => {
  selectedStatusFilter.value =
    selectedStatusFilter.value === status ? null : status;
  currentPage.value = 1;
};

const clearStatusFilter = () => {
  selectedStatusFilter.value = null;
  currentPage.value = 1;
};

const intentLabel = (request) => {
  const intent = request.form?.intent;
  if (intent === 'open_source' || request.form?.is_open_source) return 'Open source';
  if (intent === 'commercial') return 'Commercial';
  return request.listing?.is_open_source ? 'Open source' : 'Commercial';
};
</script>

<template>
  <section class="p-5 bg-echoes-background-black">
    <UsersHeader>
      <template #title>Publication Requests</template>
      <template #description>
        Review self-serve listings before they go live on the marketplace.
      </template>
    </UsersHeader>

    <div class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
      <StatCard
        class="cursor-pointer transition-all duration-300 hover:opacity-90"
        :class="[
          !selectedStatusFilter
            ? 'ring-2 ring-offset-2 ring-sky-500 dark:ring-offset-gray-800'
            : '',
          selectedStatusFilter ? 'opacity-40 grayscale hover:opacity-80 hover:grayscale-0' : '',
        ]"
        @click="clearStatusFilter()"
      >
        <template #icon>
          <i class="uil uil-file-check-alt text-2xl text-sky-500"></i>
        </template>
        <template #title>Total</template>
        <template #value>{{ requests.length }}</template>
      </StatCard>
      <StatCard
        class="cursor-pointer transition-all duration-300 hover:opacity-90"
        :class="[
          selectedStatusFilter === 'pending'
            ? 'ring-2 ring-offset-2 ring-amber-500 dark:ring-offset-gray-800'
            : '',
          selectedStatusFilter && selectedStatusFilter !== 'pending'
            ? 'opacity-40 grayscale hover:opacity-80 hover:grayscale-0'
            : '',
        ]"
        @click="filterRequests('pending')"
      >
        <template #icon>
          <i class="uil uil-clock text-2xl text-amber-500"></i>
        </template>
        <template #title>Pending</template>
        <template #value>{{
          requests.filter((r) => r.status === 'pending').length
        }}</template>
      </StatCard>
      <StatCard
        class="cursor-pointer transition-all duration-300 hover:opacity-90"
        :class="[
          selectedStatusFilter === 'approved'
            ? 'ring-2 ring-offset-2 ring-emerald-500 dark:ring-offset-gray-800'
            : '',
          selectedStatusFilter && selectedStatusFilter !== 'approved'
            ? 'opacity-40 grayscale hover:opacity-80 hover:grayscale-0'
            : '',
        ]"
        @click="filterRequests('approved')"
      >
        <template #icon>
          <i class="uil uil-check-circle text-2xl text-emerald-500"></i>
        </template>
        <template #title>Approved</template>
        <template #value>{{
          requests.filter((r) => r.status === 'approved').length
        }}</template>
      </StatCard>
      <StatCard
        class="cursor-pointer transition-all duration-300 hover:opacity-90"
        :class="[
          selectedStatusFilter === 'rejected'
            ? 'ring-2 ring-offset-2 ring-red-500 dark:ring-offset-gray-800'
            : '',
          selectedStatusFilter && selectedStatusFilter !== 'rejected'
            ? 'opacity-40 grayscale hover:opacity-80 hover:grayscale-0'
            : '',
        ]"
        @click="filterRequests('rejected')"
      >
        <template #icon>
          <i class="uil uil-times-circle text-2xl text-red-500"></i>
        </template>
        <template #title>Rejected</template>
        <template #value>{{
          requests.filter((r) => r.status === 'rejected').length
        }}</template>
      </StatCard>
    </div>

    <div class="py-6">
      <div
        class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light"
      >
        <div class="p-4">
          <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div class="w-full sm:w-96">
              <TextInput
                v-model="searchQuery"
                placeholder="Search by user or listing"
                class="w-full"
              />
            </div>
            <div class="w-full sm:w-24">
              <SelectInput
                v-model="itemsPerPage"
                :options="itemsPerPageOptions"
                :hide-search="true"
                @update:modelValue="currentPage = 1"
              />
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-echoes-border-light">
            <thead class="bg-echoes-border-light">
              <tr>
                <th
                  class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider"
                >
                  Listing
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider"
                >
                  User
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider"
                >
                  Intent
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider"
                >
                  Created
                </th>
                <th
                  class="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-echoes-border-light">
              <tr v-if="paginatedRequests.length === 0">
                <td colspan="6" class="px-6 py-10 text-center text-white/70">
                  No publication requests found
                </td>
              </tr>
              <tr
                v-for="request in paginatedRequests"
                :key="request.id"
                class="hover:bg-white/5"
              >
                <td class="px-6 py-4 text-sm text-white">
                  <div class="font-medium">
                    {{
                      request.listing?.name ||
                      request.form?.listing_name ||
                      `Listing #${request.listing_id}`
                    }}
                  </div>
                  <div class="text-xs text-white/60">
                    ID {{ request.listing_id }}
                  </div>
                </td>
                <td class="px-6 py-4 text-sm text-white">
                  <div>{{ request.user?.name || '—' }}</div>
                  <div class="text-xs text-white/60">
                    {{ request.user?.email || '' }}
                  </div>
                </td>
                <td class="px-6 py-4 text-sm text-white">
                  {{ intentLabel(request) }}
                </td>
                <td class="px-6 py-4">
                  <span
                    class="inline-flex px-2.5 py-0.5 rounded-full text-xs"
                    :class="getStatus(request.status).classes"
                  >
                    {{ getStatus(request.status).text }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-white/80">
                  {{ formatDate(request.created_at) }}
                </td>
                <td class="px-6 py-4 text-right">
                  <div
                    v-if="request.status === 'pending'"
                    class="inline-flex gap-2"
                  >
                    <PrimaryButton
                      class="!px-3 !py-1.5 text-xs"
                      :disabled="reviewingId === request.id"
                      @click="submitReview('approved', request.id)"
                    >
                      <ButtonLoading
                        v-if="isAcceptLoading && reviewingId === request.id"
                        text="Approving..."
                        size="sm"
                      />
                      <span v-else>Approve</span>
                    </PrimaryButton>
                    <button
                      class="px-3 py-1.5 text-xs rounded-md border border-red-500/60 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                      :disabled="reviewingId === request.id"
                      @click="submitReview('rejected', request.id)"
                    >
                      <ButtonLoading
                        v-if="isRejectLoading && reviewingId === request.id"
                        text="Rejecting..."
                        size="sm"
                      />
                      <span v-else>Reject</span>
                    </button>
                  </div>
                  <span v-else class="text-xs text-white/50">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="filteredRequests.length > itemsPerPage"
          class="flex items-center justify-between px-6 py-4 border-t border-echoes-border-light"
        >
          <p class="text-sm text-white/70">
            Page {{ currentPage }} of {{ totalPages }}
          </p>
          <div class="flex gap-2">
            <button
              class="px-3 py-1 text-sm text-white/80 border border-echoes-border-light rounded disabled:opacity-40"
              :disabled="currentPage <= 1"
              @click="currentPage -= 1"
            >
              Previous
            </button>
            <button
              class="px-3 py-1 text-sm text-white/80 border border-echoes-border-light rounded disabled:opacity-40"
              :disabled="currentPage >= totalPages"
              @click="currentPage += 1"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <Toast />
  </section>
</template>
